/**
 * LLM-Powered Form Schema Editor Hook
 *
 * Provides state management and API integration for LLM-assisted
 * form schema editing with draft support.
 *
 * Features:
 * - Generate suggestions from natural language prompts
 * - In-memory draft state with auto-save
 * - Undo/redo support
 * - JSON export
 * - Compliance warnings for PII fields
 *
 * Mirrors useLlmWorkflowEditor pattern (DRR-056).
 */

import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ============================================================================
// GraphQL Operations
// ============================================================================

const GENERATE_FORM_SCHEMA_SUGGESTIONS = gql`
  mutation GenerateFormSchemaSuggestions($input: GenerateFormSchemaSuggestionsInput!) {
    generateFormSchemaSuggestions(input: $input) {
      suggestions {
        suggestionId
        operation
        jsonPatch
        explanation
        confidence
        affectedFields
        warnings
        complianceImpact
        piiClassification
        safetyLevelChange
      }
      draft {
        draftId
        entityType
        subgraph
        status
        currentJson
        editHistory {
          suggestionId
          appliedAt
          appliedBy
          jsonBefore
          jsonAfter
        }
        pendingSuggestions {
          suggestionId
          operation
          jsonPatch
          explanation
          confidence
          affectedFields
          warnings
          complianceImpact
          piiClassification
          safetyLevelChange
        }
        createdAt
        updatedAt
      }
    }
  }
`;

const CREATE_FORM_SCHEMA_DRAFT = gql`
  mutation CreateFormSchemaDraft($input: CreateFormSchemaDraftInput!) {
    createFormSchemaDraft(input: $input) {
      draftId
      entityType
      subgraph
      status
      currentJson
      editHistory {
        suggestionId
        appliedAt
        appliedBy
      }
      pendingSuggestions {
        suggestionId
        operation
        jsonPatch
        explanation
        confidence
      }
      createdAt
      updatedAt
    }
  }
`;

const APPLY_FORM_SCHEMA_SUGGESTION = gql`
  mutation ApplyFormSchemaSuggestion($input: ApplyFormSchemaSuggestionInput!) {
    applyFormSchemaSuggestion(input: $input) {
      draft {
        draftId
        entityType
        subgraph
        status
        currentJson
        editHistory {
          suggestionId
          appliedAt
          appliedBy
          jsonBefore
          jsonAfter
        }
        pendingSuggestions {
          suggestionId
          operation
          jsonPatch
          explanation
          confidence
          affectedFields
          warnings
        }
        createdAt
        updatedAt
      }
      message
    }
  }
`;

const UNDO_FORM_SCHEMA_EDIT = gql`
  mutation UndoFormSchemaEdit($draftId: String!) {
    undoFormSchemaEdit(draftId: $draftId) {
      draftId
      entityType
      subgraph
      status
      currentJson
      editHistory {
        suggestionId
        appliedAt
        appliedBy
      }
      pendingSuggestions {
        suggestionId
        operation
        jsonPatch
        explanation
      }
      createdAt
      updatedAt
    }
  }
`;

const DISCARD_FORM_SCHEMA_DRAFT = gql`
  mutation DiscardFormSchemaDraft($draftId: String!) {
    discardFormSchemaDraft(draftId: $draftId)
  }
`;

const GET_FORM_SCHEMA_DRAFT = gql`
  query FormSchemaDraft($draftId: String!) {
    formSchemaDraft(draftId: $draftId) {
      draftId
      entityType
      subgraph
      status
      currentJson
      editHistory {
        suggestionId
        appliedAt
        appliedBy
        jsonBefore
        jsonAfter
      }
      pendingSuggestions {
        suggestionId
        operation
        jsonPatch
        explanation
        confidence
        affectedFields
        warnings
        complianceImpact
        piiClassification
        safetyLevelChange
      }
      createdAt
      updatedAt
    }
  }
`;

const LIST_FORM_SCHEMA_DRAFTS = gql`
  query ListFormSchemaDrafts($tenantId: String!, $userId: String!) {
    listFormSchemaDrafts(tenantId: $tenantId, userId: $userId) {
      draftId
      entityType
      subgraph
      status
      createdAt
      updatedAt
      editCount
    }
  }
`;

const EXPORT_FORM_SCHEMA_DRAFT = gql`
  query ExportFormSchemaDraft($draftId: String!) {
    exportFormSchemaDraft(draftId: $draftId)
  }
`;

const PERSIST_FORM_SCHEMA_DRAFT = gql`
  mutation PersistFormSchemaDraft($draftId: String!) {
    persistFormSchemaDraft(draftId: $draftId) {
      draftId
      entityType
      subgraph
      status
      currentJson
      createdAt
      updatedAt
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type FormSchemaEditOperation =
  | 'AddField'
  | 'ModifyField'
  | 'RemoveField'
  | 'ReorderFields'
  | 'ModifyLayout'
  | 'AddFieldGroup'
  | 'ModifyValidation'
  | 'ModifyWidget';

export type FormSchemaDraftStatus =
  | 'Active'
  | 'Paused'
  | 'PendingReview'
  | 'Approved'
  | 'Rejected'
  | 'Published'
  | 'Discarded';

export type PiiClassification =
  | 'None'
  | 'Name'
  | 'Email'
  | 'Phone'
  | 'Ssn'
  | 'Dob'
  | 'Address'
  | 'Medical'
  | 'Financial';

export interface FormSchemaEditSuggestion {
  suggestionId: string;
  operation: FormSchemaEditOperation;
  jsonPatch: string;
  explanation: string;
  confidence: number;
  affectedFields: string[];
  warnings: string[];
  complianceImpact: boolean;
  piiClassification?: PiiClassification;
  safetyLevelChange?: string;
}

export interface FormSchemaField {
  name: string;
  label: string;
  component: string;
  placeholder?: string;
  helpText?: string;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
  defaultValue?: string;
  disabled?: boolean;
  readOnly?: boolean;
  visibleWhen?: string;
  widgetProps?: string;
  order?: number;
}

export interface FormSchemaDefinition {
  module: string;
  entityType: string;
  layout: 'SinglePage' | 'Wizard' | 'Tabs' | 'TwoColumn';
  title: string;
  description?: string;
  fields: FormSchemaField[];
  steps?: Array<{
    title: string;
    description?: string;
    fields: string[];
    icon?: string;
  }>;
  actions: Array<{
    id: string;
    label: string;
    variant?: string;
    mutation?: string;
    confirmMessage?: string;
    isPrimary?: boolean;
  }>;
  version: number;
}

export interface AppliedEdit {
  suggestionId: string;
  appliedAt: string;
  appliedBy: string;
  jsonBefore?: string;
  jsonAfter?: string;
}

export interface FormSchemaDraft {
  draftId: string;
  entityType: string;
  subgraph: string;
  status: FormSchemaDraftStatus;
  currentJson: string;
  editHistory: AppliedEdit[];
  pendingSuggestions: FormSchemaEditSuggestion[];
  createdAt: string;
  updatedAt: string;
}

export interface FormSchemaDraftSummary {
  draftId: string;
  entityType: string;
  subgraph: string;
  status: FormSchemaDraftStatus;
  createdAt: string;
  updatedAt: string;
  editCount: number;
}

// ============================================================================
// State Management
// ============================================================================

interface EditorState {
  // Current draft
  draft: FormSchemaDraft | null;
  // Parsed form schema
  currentSchema: FormSchemaDefinition | null;
  // Current JSON representation
  currentJson: string;
  // Pending suggestions from LLM
  pendingSuggestions: FormSchemaEditSuggestion[];
  // Selected suggestion for preview
  selectedSuggestion: FormSchemaEditSuggestion | null;
  // Loading states
  isGenerating: boolean;
  isApplying: boolean;
  isSaving: boolean;
  // Error state
  error: string | null;
  // Undo stack (for local undo before committing)
  undoStack: FormSchemaDefinition[];
  // Redo stack
  redoStack: FormSchemaDefinition[];
  // Prompt history
  promptHistory: string[];
  // Edit mode
  editMode: 'prompt' | 'manual' | 'review';
}

type EditorAction =
  | { type: 'SET_DRAFT'; draft: FormSchemaDraft }
  | { type: 'SET_SUGGESTIONS'; suggestions: FormSchemaEditSuggestion[] }
  | { type: 'SELECT_SUGGESTION'; suggestion: FormSchemaEditSuggestion | null }
  | { type: 'APPLY_SUGGESTION_START' }
  | { type: 'APPLY_SUGGESTION_SUCCESS'; draft: FormSchemaDraft }
  | { type: 'UNDO_SUCCESS'; draft: FormSchemaDraft }
  | { type: 'SET_GENERATING'; isGenerating: boolean }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'ADD_PROMPT'; prompt: string }
  | { type: 'SET_EDIT_MODE'; mode: 'prompt' | 'manual' | 'review' }
  | { type: 'LOCAL_UPDATE'; schema: FormSchemaDefinition }
  | { type: 'LOCAL_UNDO' }
  | { type: 'LOCAL_REDO' }
  | { type: 'CLEAR_DRAFT' };

function parseSchema(json: string): FormSchemaDefinition | null {
  try {
    return JSON.parse(json) as FormSchemaDefinition;
  } catch {
    return null;
  }
}

const initialState: EditorState = {
  draft: null,
  currentSchema: null,
  currentJson: '',
  pendingSuggestions: [],
  selectedSuggestion: null,
  isGenerating: false,
  isApplying: false,
  isSaving: false,
  error: null,
  undoStack: [],
  redoStack: [],
  promptHistory: [],
  editMode: 'prompt',
};

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_DRAFT': {
      const schema = parseSchema(action.draft.currentJson);
      return {
        ...state,
        draft: action.draft,
        currentSchema: schema,
        currentJson: action.draft.currentJson,
        pendingSuggestions: action.draft.pendingSuggestions ?? [],
        error: null,
      };
    }

    case 'SET_SUGGESTIONS':
      return {
        ...state,
        pendingSuggestions: action.suggestions ?? [],
        isGenerating: false,
        editMode: 'review',
      };

    case 'SELECT_SUGGESTION':
      return {
        ...state,
        selectedSuggestion: action.suggestion,
      };

    case 'APPLY_SUGGESTION_START':
      return {
        ...state,
        isApplying: true,
      };

    case 'APPLY_SUGGESTION_SUCCESS': {
      const schema = parseSchema(action.draft.currentJson);
      return {
        ...state,
        draft: action.draft,
        currentSchema: schema,
        currentJson: action.draft.currentJson,
        pendingSuggestions: action.draft.pendingSuggestions ?? [],
        selectedSuggestion: null,
        isApplying: false,
        // Clear undo stack since we committed to backend
        undoStack: [],
        redoStack: [],
      };
    }

    case 'UNDO_SUCCESS': {
      const schema = parseSchema(action.draft.currentJson);
      return {
        ...state,
        draft: action.draft,
        currentSchema: schema,
        currentJson: action.draft.currentJson,
      };
    }

    case 'SET_GENERATING':
      return {
        ...state,
        isGenerating: action.isGenerating,
      };

    case 'SET_SAVING':
      return {
        ...state,
        isSaving: action.isSaving,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
        isGenerating: false,
        isApplying: false,
        isSaving: false,
      };

    case 'ADD_PROMPT':
      return {
        ...state,
        promptHistory: [...state.promptHistory, action.prompt],
      };

    case 'SET_EDIT_MODE':
      return {
        ...state,
        editMode: action.mode,
      };

    case 'LOCAL_UPDATE':
      if (!state.currentSchema) return state;
      return {
        ...state,
        undoStack: [...state.undoStack, state.currentSchema],
        redoStack: [],
        currentSchema: action.schema,
        currentJson: JSON.stringify(action.schema, null, 2),
      };

    case 'LOCAL_UNDO':
      if (state.undoStack.length === 0 || !state.currentSchema) return state;
      const prevSchema = state.undoStack[state.undoStack.length - 1] ?? null;
      return {
        ...state,
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, state.currentSchema],
        currentSchema: prevSchema,
        currentJson: prevSchema ? JSON.stringify(prevSchema, null, 2) : '',
      };

    case 'LOCAL_REDO':
      if (state.redoStack.length === 0 || !state.currentSchema) return state;
      const nextSchema = state.redoStack[state.redoStack.length - 1] ?? null;
      return {
        ...state,
        redoStack: state.redoStack.slice(0, -1),
        undoStack: [...state.undoStack, state.currentSchema],
        currentSchema: nextSchema,
        currentJson: nextSchema ? JSON.stringify(nextSchema, null, 2) : '',
      };

    case 'CLEAR_DRAFT':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export interface UseLlmFormSchemaEditorOptions {
  userId: string;
  tenantId: string;
  entityType: string;
  subgraph: string;
  mode?: 'CREATE' | 'EDIT' | 'APPROVE' | 'FILTER';
}

export interface UseLlmFormSchemaEditorResult {
  // State
  state: EditorState;

  // Draft management
  createDraft: () => Promise<void>;
  loadDraft: (draftId: string) => Promise<void>;
  discardDraft: () => Promise<void>;
  persistDraft: () => Promise<void>;

  // LLM suggestions
  generateSuggestions: (prompt: string, targetFieldName?: string) => Promise<void>;
  applySuggestion: (suggestionId: string) => Promise<void>;
  selectSuggestion: (suggestion: FormSchemaEditSuggestion | null) => void;

  // Editing
  undo: () => void;
  redo: () => void;
  undoFromServer: () => Promise<void>;
  updateLocalSchema: (schema: FormSchemaDefinition) => void;
  setEditMode: (mode: 'prompt' | 'manual' | 'review') => void;

  // Export
  exportJson: () => Promise<{ json: string; filename: string } | null>;

  // Utilities
  canUndo: boolean;
  canRedo: boolean;
  canUndoFromServer: boolean;
  hasUnsavedChanges: boolean;
  draftsList: FormSchemaDraftSummary[];
  draftsLoading: boolean;
  refetchDrafts: () => void;
}

export function useLlmFormSchemaEditor(
  options: UseLlmFormSchemaEditorOptions
): UseLlmFormSchemaEditorResult {
  const { userId, tenantId, entityType, subgraph, mode = 'CREATE' } = options;

  const [state, dispatch] = useReducer(editorReducer, initialState);

  // GraphQL mutations
  const [generateSuggestionsMutation] = useMutation<Record<string, any>>(GENERATE_FORM_SCHEMA_SUGGESTIONS);
  const [createDraftMutation] = useMutation<Record<string, any>>(CREATE_FORM_SCHEMA_DRAFT);
  const [applySuggestionMutation] = useMutation<Record<string, any>>(APPLY_FORM_SCHEMA_SUGGESTION);
  const [undoLastEditMutation] = useMutation<Record<string, any>>(UNDO_FORM_SCHEMA_EDIT);
  const [discardDraftMutation] = useMutation<Record<string, any>>(DISCARD_FORM_SCHEMA_DRAFT);
  const [persistDraftMutation] = useMutation<Record<string, any>>(PERSIST_FORM_SCHEMA_DRAFT);

  // GraphQL queries
  const [loadDraftQuery] = useLazyQuery<Record<string, any>>(GET_FORM_SCHEMA_DRAFT);
  const [exportDraftQuery] = useLazyQuery<Record<string, any>>(EXPORT_FORM_SCHEMA_DRAFT);

  const {
    data: draftsData,
    loading: draftsLoading,
    refetch: refetchDrafts,
  } = useQuery<Record<string, any>>(LIST_FORM_SCHEMA_DRAFTS, {
    variables: { tenantId, userId },
    fetchPolicy: 'cache-and-network',
  });

  // ========================================================================
  // Draft Management
  // ========================================================================

  const createDraft = useCallback(async () => {
    try {
      const result = await createDraftMutation({
        variables: {
          input: {
            entityType,
            subgraph,
            mode,
          },
        },
      });

      if (result.data?.createFormSchemaDraft) {
        dispatch({
          type: 'SET_DRAFT',
          draft: result.data?.createFormSchemaDraft,
        });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [createDraftMutation, entityType, subgraph, mode]);

  const loadDraft = useCallback(
    async (draftId: string) => {
      try {
        const result = await loadDraftQuery({ variables: { draftId } });

        if (result.data?.formSchemaDraft) {
          dispatch({
            type: 'SET_DRAFT',
            draft: result.data?.formSchemaDraft,
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [loadDraftQuery]
  );

  const discardDraft = useCallback(async () => {
    if (!state.draft) return;

    try {
      await discardDraftMutation({
        variables: { draftId: state.draft.draftId },
      });
      dispatch({ type: 'CLEAR_DRAFT' });
      refetchDrafts();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.draft, discardDraftMutation, refetchDrafts]);

  const persistDraft = useCallback(async () => {
    if (!state.draft) return;

    dispatch({ type: 'SET_SAVING', isSaving: true });

    try {
      const result = await persistDraftMutation({
        variables: {
          draftId: state.draft.draftId,
        },
      });

      if (result.data?.persistFormSchemaDraft) {
        dispatch({
          type: 'SET_DRAFT',
          draft: result.data?.persistFormSchemaDraft,
        });
      }

      dispatch({ type: 'SET_SAVING', isSaving: false });
      refetchDrafts();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.draft, persistDraftMutation, refetchDrafts]);

  // ========================================================================
  // LLM Suggestions
  // ========================================================================

  const generateSuggestions = useCallback(
    async (prompt: string, targetFieldName?: string) => {
      dispatch({ type: 'SET_GENERATING', isGenerating: true });
      dispatch({ type: 'ADD_PROMPT', prompt });

      try {
        const result = await generateSuggestionsMutation({
          variables: {
            input: {
              prompt,
              entityType,
              subgraph,
              mode,
              targetFieldName,
              draftId: state.draft?.draftId,
            },
          },
        });

        if (result.data?.generateFormSchemaSuggestions) {
          const { suggestions, draft } = result.data?.generateFormSchemaSuggestions;
          dispatch({ type: 'SET_DRAFT', draft });
          dispatch({ type: 'SET_SUGGESTIONS', suggestions });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [generateSuggestionsMutation, entityType, subgraph, mode, state.draft?.draftId]
  );

  const applySuggestion = useCallback(
    async (suggestionId: string) => {
      if (!state.draft) return;

      dispatch({ type: 'APPLY_SUGGESTION_START' });

      try {
        const result = await applySuggestionMutation({
          variables: {
            input: {
              draftId: state.draft.draftId,
              suggestionId,
            },
          },
        });

        if (result.data?.applyFormSchemaSuggestion) {
          dispatch({
            type: 'APPLY_SUGGESTION_SUCCESS',
            draft: result.data?.applyFormSchemaSuggestion.draft,
          });
        }
      } catch (error) {
        dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      }
    },
    [state.draft, applySuggestionMutation]
  );

  const selectSuggestion = useCallback((suggestion: FormSchemaEditSuggestion | null) => {
    dispatch({ type: 'SELECT_SUGGESTION', suggestion });
  }, []);

  // ========================================================================
  // Editing
  // ========================================================================

  const undo = useCallback(() => {
    dispatch({ type: 'LOCAL_UNDO' });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: 'LOCAL_REDO' });
  }, []);

  const undoFromServer = useCallback(async () => {
    if (!state.draft) return;

    try {
      const result = await undoLastEditMutation({
        variables: { draftId: state.draft.draftId },
      });

      if (result.data?.undoFormSchemaEdit) {
        dispatch({
          type: 'UNDO_SUCCESS',
          draft: result.data?.undoFormSchemaEdit,
        });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
    }
  }, [state.draft, undoLastEditMutation]);

  const updateLocalSchema = useCallback((schema: FormSchemaDefinition) => {
    dispatch({ type: 'LOCAL_UPDATE', schema });
  }, []);

  const setEditMode = useCallback((mode: 'prompt' | 'manual' | 'review') => {
    dispatch({ type: 'SET_EDIT_MODE', mode });
  }, []);

  // ========================================================================
  // Export
  // ========================================================================

  const exportJson = useCallback(async () => {
    if (!state.draft) return null;

    try {
      const result = await exportDraftQuery({
        variables: { draftId: state.draft.draftId },
      });

      if (result.data?.exportFormSchemaDraft) {
        return {
          json: result.data?.exportFormSchemaDraft,
          filename: `${entityType}_${subgraph}_${state.draft.draftId}.json`,
        };
      }
      return null;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', error: (error as Error).message });
      return null;
    }
  }, [state.draft, exportDraftQuery, entityType, subgraph]);

  // ========================================================================
  // Computed Values
  // ========================================================================

  const canUndo = state.undoStack.length > 0;
  const canRedo = state.redoStack.length > 0;
  const canUndoFromServer = state.draft ? (state.draft.editHistory?.length ?? 0) > 0 : false;

  const hasUnsavedChanges = useMemo(() => {
    if (!state.draft) return false;
    return state.currentJson !== state.draft.currentJson;
  }, [state.draft, state.currentJson]);

  const draftsList = useMemo((): FormSchemaDraftSummary[] => {
    return draftsData?.listFormSchemaDrafts ?? [];
  }, [draftsData]);

  return {
    state,
    createDraft,
    loadDraft,
    discardDraft,
    persistDraft,
    generateSuggestions,
    applySuggestion,
    selectSuggestion,
    undo,
    redo,
    undoFromServer,
    updateLocalSchema,
    setEditMode,
    exportJson,
    canUndo,
    canRedo,
    canUndoFromServer,
    hasUnsavedChanges,
    draftsList,
    draftsLoading,
    refetchDrafts,
  };
}

export default useLlmFormSchemaEditor;
